import { supabase } from "@/integrations/supabase/client";

const recentErrors = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;

function isDuplicate(message: string): boolean {
  const now = Date.now();
  const last = recentErrors.get(message);
  if (last && now - last < DEDUP_WINDOW_MS) return true;
  recentErrors.set(message, now);
  // Clean old entries
  for (const [key, ts] of recentErrors) {
    if (now - ts > DEDUP_WINDOW_MS) recentErrors.delete(key);
  }
  return false;
}

const FALSE_POSITIVE_PATTERNS = [
  "Font not loaded",
  "ResizeObserver loop",
  "Loading chunk",
];

async function logError(errorType: string, message: string, source: string, stackTrace?: string) {
  if (isDuplicate(`${errorType}:${message}`)) return;
  if (FALSE_POSITIVE_PATTERNS.some(p => message.includes(p))) return;
  try {
    await supabase.functions.invoke("log-site-error", {
      body: {
        error_type: errorType,
        message: message.slice(0, 2000),
        source: source.slice(0, 500),
        stack_trace: stackTrace?.slice(0, 5000) || null,
        user_agent: navigator.userAgent,
        page_path: window.location.pathname,
      },
    });
  } catch {
    // Silent fail — don't cause more errors
  }
}

export function initErrorTelemetry() {
  window.onerror = (message, source, lineno, colno, error) => {
    const msg = typeof message === "string" ? message : "Unknown error";
    const src = source ? `${source}:${lineno}:${colno}` : "unknown";
    logError("runtime_crash", msg, src, error?.stack);
    return false; // Don't suppress default handling
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const msg = event.reason?.message || event.reason?.toString() || "Unhandled promise rejection";
    const stack = event.reason?.stack;
    logError("unhandled_rejection", msg, "promise", stack);
  };
}

export { logError };
